/**
 * Centralized Analytics Utility
 *
 * Provides typed helper functions for pushing events to GTM dataLayer.
 * All event names follow GA4 recommended conventions (snake_case).
 */

import { supabase } from '@/integrations/supabase/client';
import type { BillsPageMode } from '@/types/v2';
import type {
  AnalyticsEvent,
  SignInEvent,
  SignUpEvent,
  SearchEvent,
  SearchResultClickEvent,
  ViewBillEvent,
  BillFilterAppliedEvent,
  BillSortChangedEvent,
  BillDocumentViewedEvent,
  BillTabChangedEvent,
  ViewLegislatorEvent,
  LegislatorFilterAppliedEvent,
  ViewCommitteeEvent,
  CommitteeFilterAppliedEvent,
  ViewVoteEvent,
  EcommerceItem,
} from '@/types/analytics';

// ===== Core Push Function =====

/**
 * Pushes an analytics event to the GTM dataLayer.
 * Safe to call in any environment (SSR, client).
 */
function pushToDataLayer(event: AnalyticsEvent): void {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  }
}

// ===== Authentication Events =====

export function trackSignIn(method: SignInEvent['method'] = 'email'): void {
  pushToDataLayer({ event: 'sign_in', method });
}

export function trackSignUp(method: SignUpEvent['method']): void {
  pushToDataLayer({ event: 'sign_up', method });
}

export function trackSignOut(): void {
  pushToDataLayer({ event: 'sign_out' });
}

export function trackPasswordResetRequest(): void {
  pushToDataLayer({ event: 'password_reset_request' });
}

export function trackPasswordResetComplete(): void {
  pushToDataLayer({ event: 'password_reset_complete' });
}

export function trackTwoFactorEnabled(): void {
  pushToDataLayer({ event: 'two_factor_enabled' });
}

// ===== Search Events =====

export function trackSearch(params: Omit<SearchEvent, 'event'>): void {
  pushToDataLayer({ event: 'search', ...params });
}

export function trackSearchResultClick(params: Omit<SearchResultClickEvent, 'event'>): void {
  pushToDataLayer({ event: 'search_result_click', ...params });
}

// ===== Bill Events =====

export function trackViewBill(params: Omit<ViewBillEvent, 'event'>): void {
  pushToDataLayer({ event: 'view_bill', ...params });
}

export function trackBillFilterApplied(
  filterType: BillFilterAppliedEvent['filter_type'],
  filterValue: string
): void {
  pushToDataLayer({
    event: 'bill_filter_applied',
    filter_type: filterType,
    filter_value: filterValue,
  });
}

export function trackBillSortChanged(
  sortColumn: string,
  sortDirection: BillSortChangedEvent['sort_direction']
): void {
  pushToDataLayer({
    event: 'bill_sort_changed',
    sort_column: sortColumn,
    sort_direction: sortDirection,
  });
}

export function trackBillViewModeChanged(viewMode: 'cards' | 'table'): void {
  pushToDataLayer({ event: 'bill_view_mode_changed', view_mode: viewMode });
}

export function trackBillDocumentViewed(
  billNumber: string,
  documentType: BillDocumentViewedEvent['document_type'],
  versionId?: number
): void {
  pushToDataLayer({
    event: 'bill_document_viewed',
    bill_number: billNumber,
    document_type: documentType,
    version_id: versionId,
  });
}

export function trackBillExport(billNumber: string, format: 'pdf' | 'csv' | 'json'): void {
  pushToDataLayer({ event: 'bill_export', bill_number: billNumber, format });
}

export function trackBillTabChanged(billNumber: string, tab: BillTabChangedEvent['tab']): void {
  pushToDataLayer({ event: 'bill_tab_changed', bill_number: billNumber, tab });
}

export function trackBillShared(
  billId: number,
  platform: 'threads' | 'facebook' | 'bluesky' | 'download',
  template: 'minimal' | 'bold' | 'editorial' | 'gradient'
): void {
  pushToDataLayer({ event: 'bill_shared', bill_id: billId, platform, template });
}

export function trackBillShareModalOpened(billId: number, billNumber: string): void {
  pushToDataLayer({ event: 'bill_share_modal_opened', bill_id: billId, bill_number: billNumber });
}

export function trackBillLinkCopied(billId: number): void {
  pushToDataLayer({ event: 'bill_link_copied', bill_id: billId });
}

export function trackBillsListExport(count: number, format: 'csv' | 'xlsx'): void {
  pushToDataLayer({ event: 'bills_list_export', count, format });
}

/**
 * Tracks a bill view to the backend for popularity tracking.
 * Fire-and-forget - errors are logged but not surfaced to user.
 */
export async function trackBillViewToBackend(billId: number): Promise<void> {
  try {
    const { error } = await supabase.functions.invoke('track-bill-view', {
      body: { bill_id: billId },
    });

    if (error) {
      console.error('trackBillViewToBackend error:', error);
    }
  } catch (err) {
    console.error('trackBillViewToBackend:', err);
  }
}

export function trackSocialTextCopied(
  billId: number,
  platform: 'twitter' | 'threads' | 'linkedin' | 'facebook' | 'bluesky' | 'reddit'
): void {
  pushToDataLayer({ event: 'social_text_copied', bill_id: billId, platform });
}

// ===== Discovery Events =====

export function trackDiscoveryModeChanged(mode: BillsPageMode): void {
  pushToDataLayer({ event: 'discovery_mode_changed', mode });
}

// ===== Legislator Events =====

export function trackViewLegislator(params: Omit<ViewLegislatorEvent, 'event'>): void {
  pushToDataLayer({ event: 'view_legislator', ...params });
}

export function trackLegislatorFilterApplied(
  filterType: LegislatorFilterAppliedEvent['filter_type'],
  filterValue: string
): void {
  pushToDataLayer({
    event: 'legislator_filter_applied',
    filter_type: filterType,
    filter_value: filterValue,
  });
}

export function trackLegislatorExport(legislatorId: number, format: 'pdf' | 'csv' | 'json'): void {
  pushToDataLayer({ event: 'legislator_export', legislator_id: legislatorId, format });
}

export function trackViewVotingRecord(legislatorId: number, legislatorName: string): void {
  pushToDataLayer({
    event: 'view_voting_record',
    legislator_id: legislatorId,
    legislator_name: legislatorName,
  });
}

export function trackLegislatorViewModeChanged(viewMode: 'cards' | 'table'): void {
  pushToDataLayer({ event: 'legislator_view_mode_changed', view_mode: viewMode });
}

export function trackLegislatorsListExport(count: number, format: 'csv' | 'xlsx'): void {
  pushToDataLayer({ event: 'legislators_list_export', count, format });
}

// ===== Committee Events =====

export function trackViewCommittee(params: Omit<ViewCommitteeEvent, 'event'>): void {
  pushToDataLayer({ event: 'view_committee', ...params });
}

export function trackCommitteeFilterApplied(
  filterType: CommitteeFilterAppliedEvent['filter_type'],
  filterValue: string
): void {
  pushToDataLayer({
    event: 'committee_filter_applied',
    filter_type: filterType,
    filter_value: filterValue,
  });
}

export function trackCommitteesListExport(count: number, format: 'csv' | 'xlsx'): void {
  pushToDataLayer({ event: 'committees_list_export', count, format });
}

// ===== Vote Events =====

export function trackViewVote(params: Omit<ViewVoteEvent, 'event'>): void {
  pushToDataLayer({ event: 'view_vote', ...params });
}

export function trackVoteExport(voteId: number, format: 'pdf' | 'csv' | 'json' | 'xlsx'): void {
  pushToDataLayer({ event: 'vote_export', vote_id: voteId, format });
}

export function trackVoteShare(
  voteId: number,
  platform: 'threads' | 'facebook' | 'copy_link'
): void {
  pushToDataLayer({ event: 'vote_share', vote_id: voteId, platform });
}

// ===== Billing Events =====

export function trackBeginCheckout(priceId: string, planName: string, price: number): void {
  const item: EcommerceItem = {
    item_id: priceId,
    item_name: planName,
    price,
    quantity: 1,
  };
  pushToDataLayer({
    event: 'begin_checkout',
    currency: 'USD',
    value: price,
    items: [item],
  });
}

export function trackPurchase(
  transactionId: string,
  priceId: string,
  planName: string,
  price: number
): void {
  const item: EcommerceItem = {
    item_id: priceId,
    item_name: planName,
    price,
    quantity: 1,
  };
  pushToDataLayer({
    event: 'purchase',
    transaction_id: transactionId,
    currency: 'USD',
    value: price,
    items: [item],
  });
}

export function trackSubscriptionPortalOpened(): void {
  pushToDataLayer({ event: 'subscription_portal_opened' });
}

// ===== Newsletter Events =====

export function trackNewsletterSubscribe(newsletters: string[]): void {
  pushToDataLayer({ event: 'newsletter_subscribe', newsletters });
}

// ===== Navigation Events =====

export function trackYearChanged(previousYear: number, newYear: number): void {
  pushToDataLayer({
    event: 'year_changed',
    previous_year: previousYear,
    new_year: newYear,
  });
}

// ===== Engagement Events =====

export function trackUpgradeCtaClicked(location: string, ctaText?: string): void {
  pushToDataLayer({ event: 'upgrade_cta_clicked', location, cta_text: ctaText });
}

export function trackSignInCtaClicked(location: string, ctaText?: string): void {
  pushToDataLayer({ event: 'sign_in_cta_clicked', location, cta_text: ctaText });
}
